import React, { SetStateAction, useEffect, useState, useMemo } from 'react';
import { IconButton, TextField, Select, MenuItem, Button, Autocomplete, Popper, ClickAwayListener } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ResponseConfigOption } from '../../types/common';
import { InputDataRow } from '../../types/workflow';
import { NodeRunConfigMetadata } from '../../types/nodes';
import { extractNodeID } from '../extractNodeIDs';
import { textFieldSX } from '../../constants/textFieldCSS';
import DropdownPopper from '../Common/DropdownPopper';
import InputTextBoxWithDropdown from '../Common/InputTextBoxWithDropdown';

interface InputDataSectionProps {
    nodeRunConfig: Record<string, string | null>;
    responseConfig: ResponseConfigOption[];
    nodeRunConfigMetadata?: Record<string, NodeRunConfigMetadata>;
    inputRows: InputDataRow[];
    setInputRows: (value: SetStateAction<InputDataRow[]>) => void;
    dropdownOptionIdToOptionMap: Record<string, ResponseConfigOption>;
    onSave: (action?: string, cb?: () => void, isIntermittentSave?: boolean) => void;
    nodeId: string;
    countToNameMap: Record<string, string>;
}

const InputDataSection: React.FC<InputDataSectionProps> = ({
    nodeRunConfig,
    nodeRunConfigMetadata,
    responseConfig,
    inputRows,
    setInputRows,
    dropdownOptionIdToOptionMap,
    onSave,
    nodeId,
    countToNameMap
}) => {
    const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);

    useEffect(() => {
        console.log(nodeId, currentNodeId, nodeId === currentNodeId, "nodeId / currentNodeId");
        let rows = inputRows.slice();
        if (nodeId !== currentNodeId) {
            rows = [];
        }
        setCurrentNodeId(nodeId);

        Object.entries(nodeRunConfig)
            .filter(([key]) => key !== 'code' && key !== 'post_confirmation_endpoint')
            .forEach(([key, value]) => {
                const { nodeId, dataPath } = extractNodeID(value || "") || {};
                const refNodeId = nodeId || nodeRunConfigMetadata?.[key]?.ref_node_id;
                const optionId = dataPath || value?.slice(7, -4);
                const existingRow = rows.find(row => row.key === key);
                if (!existingRow) {
                    rows.push({
                        key,
                        value: value ?? '',
                        refNodeId,
                        optionId
                    });
                }
            });

        setInputRows(rows.length ? rows : [{ key: '', value: '', refNodeId: '', optionId: '' }]);
    }, [nodeRunConfig, nodeId]);

    const handleAddRow = () => {
        setInputRows([...inputRows, { key: '', value: '', refNodeId: '', optionId: '' }]);
    };

    const handleDeleteRow = (index: number) => {
        const newRows = [...inputRows];
        newRows.splice(index, 1);
        setInputRows(newRows);
    };

    const handleKeyChange = (index: number, newKey: string) => {
        const newRows = [...inputRows];
        newRows[index].key = newKey;
        setInputRows(newRows);
    };

    const handleValueChange = (index: number, value: string, refNodeId?: string) => {
        const newRows = [...inputRows];
        newRows[index].value = value;
        newRows[index].refNodeId = refNodeId || '';
        newRows[index].optionId = refNodeId ? value : '';
        setInputRows(newRows);
    };

    return (
        <div className="p-4 pt-0 space-y-4">
            {inputRows.map((row, index) => (
                <div key={index}>
                    <div className="flex gap-4 items-start mb-2">
                        <div className="flex-1">
                            <TextField
                                size="small"
                                placeholder="Key name"
                                value={row.key}
                                onChange={(e) => handleKeyChange(index, e.target.value)}
                                onBlur={() => onSave("", () => {}, true)}
                                fullWidth
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "0.5rem" // rounded-lg equivalent
                                    }
                                }}
                            />
                        </div>
                        <IconButton onClick={() => handleDeleteRow(index)}>
                            <DeleteIcon />
                        </IconButton>
                    </div>
                    
                    <div className="mb-4 flex gap-4">
                        <InputTextBoxWithDropdown
                            editableInput={row.value || ''}
                            setEditableValue={(value, refNodeId) => {
                                handleValueChange(index, value, refNodeId);
                            }}
                            refNodeId={row.refNodeId || ''}
                            dropdownOptions={responseConfig}
                            cssOverrides="w-full"
                            saveOnBlur={() => {
                                onSave("", () => {}, true);
                            }}
                            dropdownOptionIdToOptionMap={dropdownOptionIdToOptionMap}
                            countToNameMap={countToNameMap}
                        />
                        <div className="p-5"></div>
                    </div>
                    
                    {index < inputRows.length - 1 && (
                        <div className="border-t border-gray-200 mb-4"></div>
                    )}
                </div>
            ))}


            <div className="flex justify-between mt-4">
                <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddRow}
                    variant="outlined"
                    size="small"
                >
                    Add Row
                </Button>
            </div>
        </div>
    );
};

export default InputDataSection;
